/**
 * Reason-code filter ids — one convention for every report page, the schedule
 * drawers and the stored scheduled-report JSON.
 *
 * Why two ids per reason: an event's `reason_id` (core_event_alarms) holds EITHER a
 * local `reason_code_id` OR a `cloud_reason_code_id`, depending on which stack wrote
 * it — the report procs label-join core_reason_codes both ways for the same reason.
 * `FIND_IN_SET(cea.reason_id, whereReason)` therefore only catches every event for a
 * reason if the filter carries both of its ids.
 *
 * The convention:
 *   - **Selection** (drawer checkboxes, page state) is keyed by `cloud_reason_code_id`.
 *     It is never null in the option rows; `reason_code_id` is null for cloud-only
 *     reasons, so keying by it collapses those into one "null" checkbox.
 *   - **What is sent / stored** is `expandReasonIds(selection)`: each selected cloud id
 *     plus its row's `reason_code_id`, deduped. The report Lambda passes stored
 *     `reason_filter.values` straight to the procs without expanding them, so a
 *     schedule must store the expanded set to reproduce the on-screen query.
 *   - "No Reason" is the synthetic option with both ids 0.
 *
 * Known and accepted limitation (decided 2026-09-29): nothing on an event records
 * which kind of id `reason_id` holds, and local ids share a number space with cloud
 * ids, so a collision can match an unrelated reason. The backend checks the cloud id
 * first, then falls back to reason_code_id + campus_id. This lasts until every
 * community is on cloud ids and local ids stop mattering for the filter; there is no
 * frontend fix, so do not try one here.
 */

const s = (v) => String(v);

/**
 * @param {Array<string|number>} selectedCloudIds
 * @param {Array<object>} reasonOptions — rows with cloud_reason_code_id / reason_code_id
 * @returns {string[]} cloud ids plus matching local ids, deduped, as strings
 */
export function expandReasonIds(selectedCloudIds, reasonOptions) {
  const selected = (selectedCloudIds || []).map(s);
  const options = reasonOptions || [];
  const local = selected
    .map((cid) => options.find((r) => s(r.cloud_reason_code_id) === cid)?.reason_code_id)
    .filter((id) => id != null)
    .map(s);
  return [...new Set([...selected, ...local])];
}

/** Description for a selected (cloud-keyed) reason id, falling back to the id. */
export function reasonLabel(cloudId, reasonOptions) {
  const row = (reasonOptions || []).find((r) => s(r.cloud_reason_code_id) === s(cloudId));
  return row?.reason_code_description ?? s(cloudId);
}

/**
 * Turn a stored, expanded id list back into a cloud-keyed selection.
 *
 * A stored list is normally `expandReasonIds(selection)`: each selected cloud id plus
 * its derived local id. The two id spaces OVERLAP (a local reason_code_id can equal an
 * unrelated reason's cloud id), so an id that is the local id of another stored
 * reason is read as derived, not as a selection of the reason whose cloud id it
 * happens to equal — unless that reason's OWN local id is stored too, which is
 * evidence it was selected in its own right.
 *
 * Some selections are genuinely indistinguishable once stored: picking A plus a
 * cloud-only reason whose id equals A's local id stores exactly what picking A alone
 * does. Those resolve to the smaller selection. What is SENT never changes: a final
 * pass adds back anything the selection would not re-expand to, so re-saving an
 * opened schedule always stores a superset of what it stored before (in practice the
 * same set).
 *
 * Older schedules may hold local ids only (the Environmental drawer used to key by
 * reason_code_id), so a leftover id is mapped through reason_code_id. Anything that
 * matches no option is kept as-is rather than silently dropped.
 *
 * @returns {string[]} cloud-keyed selection
 */
export function selectionFromStoredReasonIds(storedIds, reasonOptions) {
  const stored = (storedIds || []).map(s);
  const storedSet = new Set(stored);
  const options = reasonOptions || [];
  const byCloud = new Map(options.map((r) => [s(r.cloud_reason_code_id), r]));
  const localOf = (r) => (r?.reason_code_id != null ? s(r.reason_code_id) : null);
  // Local ids derived from a stored cloud id — the second half of an expansion.
  const derivedLocal = new Set(
    options
      .filter((r) => localOf(r) != null && storedSet.has(s(r.cloud_reason_code_id))
        && localOf(r) !== s(r.cloud_reason_code_id))
      .map(localOf),
  );
  const selected = new Set();
  const mapLeftover = (id) => {
    const viaLocal = options.find((r) => localOf(r) === id);
    selected.add(viaLocal ? s(viaLocal.cloud_reason_code_id) : id); // legacy local-only, or unknown
  };
  stored.forEach((id) => {
    const own = byCloud.get(id);
    if (own) {
      // A cloud id that is also another stored reason's local id counts as derived,
      // unless this reason's own local id is present as well.
      const ownLocalStored = localOf(own) != null && localOf(own) !== id && storedSet.has(localOf(own));
      if (!derivedLocal.has(id) || ownLocalStored) selected.add(id);
      return;
    }
    if (derivedLocal.has(id)) return;
    mapLeftover(id);
  });
  // Safety net: the selection must re-expand to everything that was stored.
  const reExpanded = new Set(expandReasonIds([...selected], options));
  stored.forEach((id) => {
    if (reExpanded.has(id)) return;
    if (byCloud.has(id)) selected.add(id); else mapLeftover(id);
  });
  return [...selected];
}
